import { IBusinessRepository } from '../../domain/interfaces/IBusinessRepository';
import {
  Business,
  BusinessListItemDto,
  BusinessDetailDto,
  BusinessSummary,
  CreateBusinessCommand,
  UpdateBusinessCommand
} from '../../domain/entities/Business';
import { PaginatedResult, PaginationParams } from '../../domain/common/PaginatedResult';
import { UserRole } from '../../domain/value-objects/UserRole';

/**
 * BusinessService - Application service for business operations
 *
 * Orchestrates business CRUD and search operations
 */
export class BusinessService {
  constructor(private readonly businessRepository: IBusinessRepository) {}

  /**
   * Get all businesses with pagination and optional search (admin only)
   * Returns BusinessListItemDto for list display with statistics
   */
  async getAllBusinesses(
    params?: PaginationParams,
    userRole?: UserRole,
    searchTerm?: string
  ): Promise<PaginatedResult<BusinessListItemDto>> {
    // Only admins can view all businesses
    if (userRole && userRole !== 'Admin') {
      throw new Error('Only admins can view all businesses');
    }

    return this.businessRepository.getAll(params, searchTerm);
  }

  /**
   * Get a single business by ID with full details
   * - Admin: Can view any business
   * - Customer: Can only view their own business
   * Returns BusinessDetailDto for detail pages
   */
  async getBusinessById(
    id: string,
    userRole: UserRole,
    userBusinessId: string | null
  ): Promise<BusinessDetailDto> {
    // Admin can view any business
    if (userRole === 'Admin') {
      return this.businessRepository.getById(id);
    }

    // BusinessOwner can only view their own business
    if (userRole === 'BusinessOwner') {
      if (id !== userBusinessId) {
        throw new Error('Access denied: You can only view your own business');
      }
      return this.businessRepository.getById(id);
    }

    throw new Error(`Unknown user role: ${userRole}`);
  }

  /**
   * Search businesses by name for autocomplete
   * Used primarily in registration flow
   * SESSION_137: Added optional regionId to filter results by region (for company management)
   */
  async searchBusinesses(query: string, limit: number = 10, regionId?: string): Promise<BusinessSummary[]> {
    // Search is public - needed for registration
    if (!query || query.trim().length === 0) {
      return [];
    }

    return this.businessRepository.search(query, limit, regionId);
  }

  /**
   * Get business summary by ID for registration pre-population
   * SESSION_276: Public endpoint - no authentication required
   * Returns null if business not found
   */
  async getBusinessSummaryById(id: string): Promise<BusinessSummary | null> {
    if (!id || id.trim().length === 0) {
      return null;
    }
    return this.businessRepository.getSummaryById(id);
  }

  /**
   * Get all businesses for a company by company ID
   * SESSION_276: Fast registration pre-population - avoids slow text search
   * Public endpoint - no authentication required
   */
  async getBusinessesByCompanyId(companyId: string): Promise<BusinessSummary[]> {
    if (!companyId || companyId.trim().length === 0) {
      return [];
    }
    return this.businessRepository.getByCompanyId(companyId);
  }

  /**
   * Create a new business
   * - Admin: Can create any business
   * - Customer: Can create during registration (no role check needed)
   */
  async createBusiness(
    command: CreateBusinessCommand,
    userRole?: UserRole
  ): Promise<Business> {
    // Validate required fields
    if (!command.name || !command.category || !command.regionId) {
      throw new Error('Missing required fields: name, category, regionId');
    }

    // Allow creation during registration (no role) or by admin
    if (userRole && userRole !== 'Admin') {
      throw new Error('Only admins can create businesses after registration');
    }

    return this.businessRepository.create(command);
  }

  /**
   * Create or claim a business during registration
   * This is a special flow that doesn't require role checks
   */
  async createOrClaimBusiness(
    searchQuery: string,
    createCommand: CreateBusinessCommand
  ): Promise<{ business: BusinessDetailDto | Business; claimed: boolean }> {
    // First, search for existing businesses
    const existingBusinesses = await this.searchBusinesses(searchQuery, 5);

    // If exact match found, "claim" it (return existing)
    const exactMatch = existingBusinesses.find(
      (b) => b.name.toLowerCase() === searchQuery.toLowerCase()
    );

    if (exactMatch) {
      const business = await this.businessRepository.getById(exactMatch.id);
      return { business, claimed: true };
    }

    // If no match, create new business
    const newBusiness = await this.businessRepository.create(createCommand);
    return { business: newBusiness, claimed: false };
  }

  /**
   * Update a business
   * - Admin: Can update any business
   * - Customer: Can update their own business (limited fields)
   */
  async updateBusiness(
    id: string,
    command: UpdateBusinessCommand,
    userRole: UserRole,
    userBusinessId: string | null
  ): Promise<Business> {
    // Admin can update any business
    if (userRole === 'Admin') {
      return this.businessRepository.update(id, command);
    }

    // BusinessOwner can only update their own business
    if (userRole === 'BusinessOwner') {
      if (id !== userBusinessId) {
        throw new Error('Access denied: You can only update your own business');
      }

      // Customers have limited update permissions (no category/verification changes)
      const allowedFields: (keyof UpdateBusinessCommand)[] = [
        'description',
        'phone',
        'website',
        'email',
        'openingHours'
      ];

      // Filter command to only allowed fields - preserve required fields
      const filteredCommand: UpdateBusinessCommand = {
        id: command.id,
        name: command.name,
        category: command.category
      };

      for (const key of allowedFields) {
        if (command[key] !== undefined) {
          (filteredCommand as any)[key] = command[key];
        }
      }

      return this.businessRepository.update(id, filteredCommand);
    }

    throw new Error(`Unknown user role: ${userRole}`);
  }

  /**
   * Deactivate a business (admin only)
   * Sprint 045: Sets IsActive = false, reprocesses mentions
   */
  async deactivateBusiness(id: string, userRole: UserRole): Promise<void> {
    if (userRole !== 'Admin') {
      throw new Error('Only admins can deactivate businesses');
    }
    await this.businessRepository.deactivate(id);
  }

  /**
   * Reactivate a previously deactivated business (admin only)
   * Sprint 045: Sets IsActive = true, reprocesses mentions
   */
  async reactivateBusiness(id: string, userRole: UserRole): Promise<void> {
    if (userRole !== 'Admin') {
      throw new Error('Only admins can reactivate businesses');
    }
    await this.businessRepository.reactivate(id);
  }
}
